import { useState } from 'react'
import { Modal, Button, Form, Alert, Badge, Row, Col } from 'react-bootstrap'

const LogExportModal = ({ show, onHide, exportLogs, getLogStats }) => {
  const [exportFormat, setExportFormat] = useState('json')
  const [exportStatus, setExportStatus] = useState(null)

  const handleExport = () => {
    try {
      const exportedData = exportLogs(exportFormat)
      
      // 创建下载链接
      const blob = new Blob([exportedData], { 
        type: exportFormat === 'json' ? 'application/json' : 'text/plain' 
      })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `daily-tasks-logs-${new Date().toISOString().split('T')[0]}.${exportFormat === 'json' ? 'json' : exportFormat === 'csv' ? 'csv' : 'txt'}`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
      
      setExportStatus({ type: 'success', message: '日志导出成功！' })
      
      // 3秒后自动关闭状态提示
      setTimeout(() => setExportStatus(null), 3000)
    } catch (error) {
      setExportStatus({ type: 'error', message: `导出失败: ${error.message}` })
    }
  }

  const handleCopyToClipboard = async () => {
    try {
      const exportedData = exportLogs(exportFormat)
      await navigator.clipboard.writeText(exportedData)
      setExportStatus({ type: 'success', message: '已复制到剪贴板！' })
      setTimeout(() => setExportStatus(null), 3000)
    } catch (error) {
      setExportStatus({ type: 'error', message: '复制失败，请手动选择文本' })
    }
  }

  const stats = getLogStats()

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>日志管理</Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        {/* 日志统计信息 */}
        <div className="mb-4">
          <h6>日志统计</h6>
          <Row>
            <Col md={6}>
              <div className="d-flex flex-wrap gap-2 mb-2">
                <Badge bg="secondary">总计: {stats.total}</Badge>
                {Object.entries(stats.byType).map(([type, count]) => (
                  <Badge 
                    key={type} 
                    bg={type === 'error' ? 'danger' : type === 'warning' ? 'warning' : type === 'success' ? 'success' : 'info'}
                  >
                    {type}: {count}
                  </Badge>
                ))}
              </div>
            </Col>
            <Col md={6}>
              {stats.oldestTimestamp && (
                <small className="text-muted">
                  <div>最早: {stats.oldestTimestamp}</div>
                  <div>最新: {stats.newestTimestamp}</div>
                </small>
              )}
            </Col>
          </Row>
        </div>

        {/* 导出选项 */}
        <div className="mb-4">
          <h6>导出格式</h6>
          <Form.Group>
            <div className="d-flex gap-3">
              <Form.Check
                type="radio"
                id="format-json"
                label="JSON (结构化数据)"
                name="exportFormat"
                value="json"
                checked={exportFormat === 'json'}
                onChange={(e) => setExportFormat(e.target.value)}
              />
              <Form.Check
                type="radio"
                id="format-text"
                label="文本 (可读格式)"
                name="exportFormat"
                value="text"
                checked={exportFormat === 'text'}
                onChange={(e) => setExportFormat(e.target.value)}
              />
              <Form.Check
                type="radio"
                id="format-csv"
                label="CSV (表格格式)"
                name="exportFormat"
                value="csv"
                checked={exportFormat === 'csv'}
                onChange={(e) => setExportFormat(e.target.value)}
              />
            </div>
          </Form.Group>
        </div>

        {/* 预览 */}
        <div className="mb-4">
          <h6>预览 (前5条)</h6>
          <div 
            className="border rounded p-2 bg-light" 
            style={{ 
              maxHeight: '200px', 
              overflow: 'auto', 
              fontSize: '12px',
              fontFamily: 'monospace'
            }}
          >
            {exportLogs(exportFormat).split('\n').slice(0, 5).join('\n')}
            {stats.total > 5 && '\n...'}
          </div>
        </div>

        {/* 状态提示 */}
        {exportStatus && (
          <Alert variant={exportStatus.type === 'success' ? 'success' : 'danger'}>
            {exportStatus.message}
          </Alert>
        )}

        {/* 最近日志 */}
        {stats.recent.length > 0 && (
          <div>
            <h6>最近日志</h6>
            <div className="border rounded p-2" style={{ maxHeight: '150px', overflow: 'auto' }}>
              {stats.recent.map(log => (
                <div key={log.id} className="small mb-1">
                  <Badge 
                    bg={log.type === 'error' ? 'danger' : log.type === 'warning' ? 'warning' : log.type === 'success' ? 'success' : 'info'}
                    className="me-2"
                  >
                    {log.type}
                  </Badge>
                  <span className="text-muted me-2">{log.timestamp.split(' ')[1]}</span>
                  <span>{log.message}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </Modal.Body>
      
      <Modal.Footer>
        <div className="d-flex gap-2 w-100">
          <Button variant="outline-secondary" onClick={handleCopyToClipboard}>
            复制到剪贴板
          </Button>
          <Button variant="primary" onClick={handleExport}>
            下载文件
          </Button>
          <Button variant="secondary" onClick={onHide} className="ms-auto">
            关闭
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  )
}

export default LogExportModal
